<script setup>
import { useStore } from 'vuex';
import { watch, computed, ref, onMounted } from 'vue';
import MyLoadsDesktopTableComponent from '@/components/myLoads/MyLoadsDesktopTableComponent.vue';
import { useRouter } from 'vue-router';
import MyLoadsDesktopFiltersComponent from '@/components/myLoads/MyLoadsDesktopFiltersComponent.vue';
import TeleportModalWrapperComponent from '@/components/TeleportModalWrapperComponent.vue';
import MyLoadsMobileMain from '@/components/myLoads/MyLoadsMobileMain.vue';
import { useScreenSize } from '@/composables/useScreenSize';
import { ButtonComponent } from '@/components';

const { screenSize } = useScreenSize();

const store = useStore();
const router = useRouter();
const name = computed(() => router.currentRoute.value.name);

const isWebView =
  typeof window !== 'undefined' &&
  typeof window.ReactNativeWebView !== 'undefined';

const emit = defineEmits(['connectEldClick']);

const myLoadsTabs = {
  unassigned: { title: 'Action Needed' },
  dispatched: { title: 'Covered' },
  'in-transit': { title: 'In Transit' },
  completed: { title: 'Completed' },
};

const userIsADriver = computed(() => store.getters['users/userIsADriver']);

const pageTabs = computed(() => {
  if (userIsADriver.value) {
    // Driver users can only see In Transit and Completed tabs
    return {
      'in-transit': myLoadsTabs['in-transit'],
      completed: myLoadsTabs.completed,
    };
  }
  return myLoadsTabs;
});

const activePageTab = computed(
  () => router.currentRoute.value.query.tab || Object.keys(pageTabs.value)[0]
);

const isLoading = computed(
  () => store.state.myLoadsStore[activePageTab.value].isLoading
);
const loads = computed(() => {
  switch (activePageTab.value) {
    case 'unassigned':
      return store.state.myLoadsStore.unassigned.loads;
    case 'dispatched':
      return store.state.myLoadsStore.dispatched.loads;
    case 'in-transit':
      return store.state.myLoadsStore['in-transit'].loads;
    case 'completed':
      return store.state.myLoadsStore.completed.loads;
    default:
      return store.state.myLoadsStore.unassigned.loads;
  }
});

const canViewMyLoads = computed(() => {
  return (
    store.getters['users/userIsADriver'] ||
    store.state.users.currentUser?.scopes?.includes('carrier_load_abilities')
  );
});

const canSearchBrokers = computed(
  () => store.state.users.featureFlags?.carrier_can_search_brokers?.enabled
);

const fetchAll = () => {
  // Always fetch equipment types
  store.dispatch('loads/getEquipmentTypes');

  // Only fetch myLoadsStore data if canViewMyLoads is true
  if (canViewMyLoads.value) {
    if (userIsADriver.value) {
      // Driver users only fetch In Transit and Completed tabs
      store.dispatch('myLoadsStore/getMyLoads', 'in-transit');
      store.dispatch('myLoadsStore/getMyLoads', 'completed');
    } else {
      store.dispatch('myLoadsStore/getMyLoads', 'unassigned');
      store.dispatch('myLoadsStore/getMyLoads', 'dispatched');
      store.dispatch('myLoadsStore/getMyLoads', 'in-transit');
      store.dispatch('myLoadsStore/getMyLoads', 'completed');
    }
  }
  if (canSearchBrokers.value) {
    store.dispatch('myLoadsStore/getCarrierBrokers');
  }
};

// helper for checking query object emptiness
const isEmpty = (obj) => {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false;
    }
  }
  return true;
};

const getTabQuery = (tab) => {
  return store.state.myLoadsStore[tab].query;
};
const getIsTabLoading = (tab) => {
  return store.state.myLoadsStore[tab].isLoading;
};
const getTabTotalRecords = (tab) => {
  return store.state.myLoadsStore[tab].pagination.totalRecords;
};

onMounted(() => {
  // on page load, check if query exists
  if (router.currentRoute.value.query.tab) {
    const queryTab = router.currentRoute.value.query.tab;
    // If driver user and tab is not allowed, redirect to default tab
    if (
      userIsADriver.value &&
      !['in-transit', 'completed'].includes(queryTab)
    ) {
      router.push({
        query: getTabQuery('in-transit'),
      });
      return;
    }
    // if query, save query and fetch data for the current tab
    // set query in store
    store.commit('myLoadsStore/setQuery', {
      tab: activePageTab.value,
      query: router.currentRoute.value.query,
    });
    // fetch all tabs after setting query
    fetchAll();
  } else if (name.value === 'CarrierWelcomePage') {
    router.push({});
    fetchAll();
  } else {
    // if no query, trigger query watcher by pushing last query
    // For driver users, default to 'in-transit' if no query exists
    const defaultTab = userIsADriver.value ? 'in-transit' : activePageTab.value;
    router.push({
      query: getTabQuery(defaultTab),
    });
  }
});

const hasFetchedAll = computed(() => {
  if (userIsADriver.value) {
    // Driver users only need In Transit and Completed to be loaded
    return (
      store.state.myLoadsStore['in-transit'].hasLoaded &&
      store.state.myLoadsStore.completed.hasLoaded
    );
  }
  return (
    store.state.myLoadsStore.unassigned.hasLoaded &&
    store.state.myLoadsStore.dispatched.hasLoaded &&
    store.state.myLoadsStore['in-transit'].hasLoaded &&
    store.state.myLoadsStore.completed.hasLoaded
  );
});

// on query changes, fetch data
watch(
  () => router.currentRoute.value.query,
  (newValue, oldValue) => {
    if (name.value !== 'My Loads') {
      return;
    }
    // if no tab, trigger query watcher by pushing last query
    if (isEmpty(newValue) || !newValue.tab) {
      router.push({
        query: getTabQuery(activePageTab.value),
      });
      return;
    }
    if (newValue.driver !== oldValue.driver) {
      fetchAll();
      return;
    }
    // fetch tab data if same tab
    if (newValue.tab === oldValue.tab) {
      store.commit('myLoadsStore/setQuery', {
        tab: newValue.tab,
        query: newValue,
      });
      store.dispatch('myLoadsStore/getMyLoads', newValue.tab);
    } else if (!hasFetchedAll.value) {
      fetchAll();
    }
  }
);

const setActivePageTab = (tab) => {
  router.push({
    query: getTabQuery(tab),
  });
};

const isTablet = computed(() => screenSize.value === 'tablet');

const isMobile = computed(() => screenSize.value === 'mobile');

const showFilters = ref(isTablet.value ? false : true);

const showRefreshButton = ref(false);
const refreshButtonClicks = ref(0);
const handleRefreshButtonClick = () => {
  refreshButtonClicks.value += 1;
  if (refreshButtonClicks.value >= 2) {
    showRefreshButton.value = false;
    refreshButtonClicks.value = 0;
  }
  fetchAll();
};
const handleShowRefreshButton = () => {
  refreshButtonClicks.value = 0;
  setTimeout(() => {
    showRefreshButton.value = true;
  }, 1500);
};

watch(
  () => isTablet.value,
  (newValue) => {
    if (newValue) {
      showFilters.value = false;
    } else {
      showFilters.value = true;
    }
  },
  { immediate: true }
);

const handleCloseFiltersModal = () => {
  if (isTablet.value) {
    showFilters.value = false;
  }
};
</script>

<template>
  <div>
    <MyLoadsMobileMain
      v-if="(name === 'My Loads' && isMobile) || name !== 'My Loads'"
      :page-tabs="pageTabs"
      @connect-eld-click="emit('connectEldClick')"
      @set-active-page-tab="(tab) => setActivePageTab(tab)"
    />
    <div
      v-else
      class="table-max m-auto px-6"
    >
      <div class="flex w-full flex-col pt-10">
        <div class="flex w-full items-center justify-between">
          <div class="flex w-full items-center justify-between gap-4">
            <h2
              v-if="!isWebView"
              class="whitespace-nowrap text-3xl text-dark-gray"
            >
              My Loads
            </h2>
            <ButtonComponent
              v-if="showRefreshButton"
              class="gap-2"
              @click="handleRefreshButtonClick"
            >
              <img
                src="@/assets/refresh.svg"
                alt="refresh"
              />
              Refresh View
            </ButtonComponent>
            <ButtonComponent
              class="gap-1"
              @click="showFilters = !showFilters"
            >
              <img
                src="@/assets/filters-icon.svg"
                alt="filters"
              />
              {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
            </ButtonComponent>
          </div>
        </div>
        <div class="mb-2 mt-5 flex w-full items-center justify-between">
          <div class="flex w-full items-center gap-8 pt-2">
            <div
              v-for="tab in Object.keys(pageTabs)"
              :key="`${tab}-link`"
              :title="`Change to ${pageTabs[tab]?.title}`"
              class="tab flex cursor-pointer items-center gap-1 whitespace-nowrap rounded text-base leading-tight tracking-tight"
              :class="{
                'text-steel-700': activePageTab !== tab,
                'text-sun-400': activePageTab === tab,
              }"
              @click="setActivePageTab(tab)"
            >
              <div class="flex flex-col">
                <div class="flex gap-1">
                  <div>{{ pageTabs[tab]?.title }}</div>
                  <div
                    :class="[
                      {
                        'skeleton-animation ml-1 mt-0.5 h-4 w-4 rounded-full text-transparent':
                          getIsTabLoading(tab),
                      },
                      getIsTabLoading(tab)
                        ? activePageTab === tab
                          ? 'bg-sun-400'
                          : 'bg-steel-700'
                        : 'bg-white',
                    ]"
                  >
                    {{
                      getIsTabLoading(tab) ? '' : `(${getTabTotalRecords(tab)})`
                    }}
                  </div>
                </div>
                <div
                  v-if="activePageTab === tab && !getIsTabLoading(tab)"
                  class="h-0.5 w-full rounded-sm bg-sun-400"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="mt-4 flex w-full"
        :class="isTablet ? 'gap-0' : 'gap-4'"
      >
        <TeleportModalWrapperComponent
          v-if="showFilters"
          :should-teleport="isTablet"
          :is-modal-open="showFilters"
        >
          <MyLoadsDesktopFiltersComponent
            v-if="showFilters"
            :active-tab="activePageTab"
            :is-loading="isLoading"
            @close-modal="handleCloseFiltersModal"
          />
        </TeleportModalWrapperComponent>
        <!-- results section -->
        <div
          class="w-full overflow-auto rounded-md pb-4"
          :class="showFilters ? 'flex-1' : 'w-full'"
        >
          <!-- no data -->
          <div
            v-if="!loads.length || isLoading"
            class="flex h-96 justify-center rounded-lg bg-off-white"
          >
            <div
              class="inner-container flex flex-col items-center justify-center p-8"
            >
              <div
                v-if="isLoading"
                class="mx-auto h-16 w-16 text-center"
              >
                <img
                  class="loading relative w-full"
                  src="@/assets/loading-v2-yellow.svg"
                  alt="loading"
                />
              </div>
              <div
                v-else
                class="magnify-image"
              >
                <img
                  class="-top-2px relative w-full text-yellow-500"
                  src="@/assets/magnify.svg"
                  alt=""
                />
              </div>
              <div class="mt-4 text-gray-over-dark">
                <h3
                  v-if="isLoading"
                  class="text-center text-lg"
                >
                  Loading
                </h3>
                <h3
                  v-else
                  class="text-center text-lg"
                >
                  No results found for
                  {{ pageTabs[activePageTab]?.title.toLowerCase() }}.
                </h3>
              </div>
            </div>
          </div>
          <!-- data -->
          <MyLoadsDesktopTableComponent
            v-else
            @show-refresh-button="handleShowRefreshButton"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
